import { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Co-BDE - the supply-side twin of the BDE (7 Sep 2026).
// Segments of the candidate pool -> who is hiring for exactly that -> a
// mandate pitch that leads with the bench. Admin only, behind
// settings.co_bde_enabled (default OFF). This page never sends anything:
// "Queue" parks a lead for the BDE engine, whose own gates decide sending.

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('ats_token')}`,
  'Content-Type': 'application/json',
});

async function api(path, opts = {}) {
  const res = await fetch(`${API_URL}/api/co-bde${path}`, { ...opts, headers: authHeaders() });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const detail = (data && (data.detail || data.message)) || `${res.status} ${res.statusText}`;
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return data;
}

const fmtInr = (n) => (n || n === 0 ? `₹${Math.round(n).toLocaleString('en-IN')}` : '—');
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const STATUS_TONE = {
  new: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  existing_client: 'bg-amber-50 text-amber-800 border-amber-200',
  queued: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  contacted_pending: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  contacted: 'bg-blue-50 text-blue-700 border-blue-200',
  replied: 'bg-purple-50 text-purple-700 border-purple-200',
  mandate: 'bg-green-100 text-green-800 border-green-300',
  placed: 'bg-green-200 text-green-900 border-green-400',
  dismissed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const Toggle = ({ label, value, onChange, hint }) => (
  <label className="flex items-center justify-between gap-3 py-1.5 text-sm">
    <span>
      <span className="text-slate-800">{label}</span>
      {hint ? <span className="block text-[11px] text-slate-400">{hint}</span> : null}
    </span>
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
  </label>
);

const NumberField = ({ label, value, onChange, hint }) => (
  <label className="flex items-center justify-between gap-3 py-1.5 text-sm">
    <span>
      <span className="text-slate-800">{label}</span>
      {hint ? <span className="block text-[11px] text-slate-400">{hint}</span> : null}
    </span>
    <Input type="number" className="w-24 h-8" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
  </label>
);

export default function CoBDEPage() {
  const [settings, setSettings] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [segments, setSegments] = useState([]);
  const [built, setBuilt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [opps, setOpps] = useState([]);
  const [usage, setUsage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState('');
  const [pitch, setPitch] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [templateKey, setTemplateKey] = useState('co_bde_supply_pitch_v1');
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewRelease, setReviewRelease] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, p, seg, u, t, rv] = await Promise.all([
        api('/settings'), api('/pipeline'), api('/segments'), api('/usage'), api('/templates'), api('/review/queue'),
      ]);
      setSettings(s); setPipeline(p); setSegments(seg.segments || []); setBuilt(seg.built || null);
      setUsage(u); setTemplates(t.templates || []);
      setReviewQueue(rv.queue || []); setReviewRelease(rv.release || null);
      if (s.default_template_key) setTemplateKey(s.default_template_key);
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  const loadOpps = useCallback(async (key, status) => {
    try {
      const params = new URLSearchParams();
      if (key) params.set('segment', key);
      if (status) params.set('status', status);
      const r = await api(`/opportunities?${params.toString()}`);
      setOpps(r.opportunities || []);
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadOpps(selected, statusFilter); }, [selected, statusFilter, loadOpps]);

  const enabled = !!(settings && settings.co_bde_enabled);

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const r = await fn();
      await loadAll();
      await loadOpps(selected, statusFilter);
      return r;
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const saveSetting = (patch) => run('settings', async () => {
    const s = await api('/settings', { method: 'POST', body: JSON.stringify(patch) });
    setSettings(s);
    toast.success('Settings saved');
  });

  const rebuild = () => run('rebuild', async () => {
    const r = await api('/rebuild', { method: 'POST' });
    toast.success(`Supply map: ${r.segments} segments, ${r.segments_above_minimum} above minimum (${r.candidates_scanned} candidates scanned)`);
  });

  const refresh = () => run('refresh', async () => {
    const r = await api('/opportunities/refresh', { method: 'POST', body: JSON.stringify(selected ? { segment_keys: [selected] } : {}) });
    const planned = (r.runs || []).reduce((a, x) => a + (x.planned || 0), 0);
    toast.success(`Demand: ${r.created} new, ${r.updated} refreshed across ${r.segments} segments${planned ? `; ${planned} external queries planned (dry-run)` : ''}`);
  });

  const preview = (opp) => run('pitch', async () => {
    const r = await api(`/opportunities/${opp.id}/pitch`, { method: 'POST', body: JSON.stringify({ template_key: templateKey }) });
    setPitch(r);
  });

  const queue = (opp) => run('queue', async () => {
    const r = await api(`/opportunities/${opp.id}/queue`, { method: 'POST', body: JSON.stringify({ template_key: templateKey }) });
    toast.success(`Parked for the BDE as lead ${r.lead_id.slice(0, 8)} (stage imported${r.needs_contact ? ', needs a contact' : ''})`);
    setPitch(null);
  });

  const dismiss = (opp) => run('dismiss', async () => {
    await api(`/opportunities/${opp.id}/dismiss`, { method: 'POST', body: JSON.stringify({ reason: 'dismissed on the Co-BDE page' }) });
  });

  const mandate = (opp) => run('mandate', async () => {
    const r = await api(`/opportunities/${opp.id}/mandate`, { method: 'POST' });
    toast.success(r.created ? 'Draft requirement created (status prospect) + task' : 'Requirement already exists');
  });

  const release = (dry) => run('release', async () => {
    const r = await api(`/release?dry_run=${dry ? 1 : 0}`, { method: 'POST' });
    toast.success(r.skipped ? `Release: ${r.skipped}` : dry ? `Would release ${r.would_release}` : `Released ${r.released} to the BDE funnel`);
  });

  const syncReplies = () => run('replies', async () => {
    const r = await api('/replies/sync', { method: 'POST' });
    toast.success(`Replies: ${r.scanned} scanned, ${r.positive} positive, ${r.requirements_created} draft requirements`);
  });

  const approvePitch = (row) => run('review-approve', async () => {
    if (!window.confirm(`Approve and release the pitch to ${row.company}? This moves it into the BDE funnel — the BDE engine's own approval, caps and hold rules still decide if and when anything sends.`)) return;
    const r = await api(`/review/${row.lead_id}/approve`, { method: 'POST' });
    toast.success(`Approved — moved to the BDE funnel (stage ${r.stage}). Nothing was sent.`);
  });

  const discardPitch = (row) => run('review-discard', async () => {
    if (!window.confirm(`Discard the pitch to ${row.company}? It stays in the data and can be reversed there — it just won't move forward.`)) return;
    await api(`/review/${row.lead_id}/discard`, { method: 'POST', body: JSON.stringify({ reason: 'discarded on the Co-BDE page' }) });
    toast.success('Discarded.');
  });

  const visibleSegments = useMemo(() => (showAll ? segments : segments.filter((s) => s.above_minimum)), [segments, showAll]);
  const selectedSeg = useMemo(() => segments.find((s) => s.key === selected) || null, [segments, selected]);
  const approvedKeys = useMemo(() => new Set(templates.filter((t) => t.approval && t.approval.owner_approved).map((t) => t.key)), [templates]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="p-6 space-y-6 max-w-[1400px] mx-auto max-md:p-4 max-md:pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Co-BDE — supply-led mandates</h1>
            <p className="text-sm text-slate-500 mt-1">
              Start from the candidates we hold, find who is hiring for exactly those profiles, pitch the bench. Nothing on this page sends an email — a queued pitch is handed to the BDE engine, whose own approval, caps and hold decide.
            </p>
          </div>
          <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap max-md:[&>button]:w-full max-md:[&>button]:min-h-[44px]">
            <Badge className={enabled ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-700'}>{enabled ? 'ON' : 'OFF'}</Badge>
            <Button variant={enabled ? 'outline' : 'default'} disabled={!!busy} onClick={() => saveSetting({ co_bde_enabled: !enabled })}>
              {enabled ? 'Switch off' : 'Switch on'}
            </Button>
            <Button variant="outline" disabled={!!busy || !enabled} onClick={rebuild}>{busy === 'rebuild' ? 'Rebuilding…' : 'Rebuild supply map'}</Button>
            <Button variant="outline" disabled={!!busy || !enabled} onClick={refresh}>{busy === 'refresh' ? 'Searching…' : selected ? 'Find demand (this segment)' : 'Find demand (top segments)'}</Button>
          </div>
        </div>

        {/* Funnel */}
        {pipeline && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {pipeline.funnel.map((f) => (
              <Card key={f.stage} className="shadow-sm">
                <CardContent className="p-4">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400">{f.stage}</p>
                  <p className="text-2xl font-bold text-slate-900">{f.n}</p>
                  <p className="text-[11px] text-slate-500 truncate" title={f.detail}>{f.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {pipeline && (
          <p className="text-xs text-slate-500">
            Conversion: opportunity→pitch {pct(pipeline.conversion.opportunity_to_pitch)} · pitch→contact {pct(pipeline.conversion.pitch_to_contact)} · contact→reply {pct(pipeline.conversion.contact_to_reply)} · reply→mandate {pct(pipeline.conversion.reply_to_mandate)} · mandate→placement {pct(pipeline.conversion.mandate_to_placement)}
          </p>
        )}

        {/* Review bay: every parked Co-BDE pitch, waiting for a human decision */}
        <Card className="shadow-sm" style={{ background: '#F7F1E3', border: '1px solid #E6DAC0' }}>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 py-3">
            <CardTitle className="text-base" style={{ color: '#2A241A' }}>Queued pitches — waiting for you</CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <span className="inline-flex items-center rounded-full px-3 py-1 font-semibold" style={{ background: '#AD8A2E', color: '#FFFDF8' }}>
                {reviewQueue.length} waiting
              </span>
              {reviewRelease && (
                <span style={{ color: '#8a7d61' }}>Daily release cap: {reviewRelease.remaining}/{reviewRelease.daily_cap} left today</span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {reviewQueue.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: '#8a7d61' }}>Nothing waiting — queue a pitch from the opportunities list below.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {reviewQueue.map((row) => (
                  <div key={row.lead_id} className="rounded-lg p-4 flex flex-col gap-2" style={{ background: '#FFFDF8', border: '1px solid #E6DAC0' }}>
                    <div>
                      <p className="font-semibold" style={{ color: '#2A241A' }}>{row.company} <span style={{ color: '#AD8A2E' }}>·</span> {row.role}</p>
                      <p className="text-xs" style={{ color: '#8a7d61' }}>
                        {row.location || '—'} · est. fee {fmtInr(row.estimated_fee)} · score {row.score ?? '—'}
                        {row.source_url ? <> · <a href={row.source_url} target="_blank" rel="noreferrer" className="underline" style={{ color: '#AD8A2E' }}>posting</a></> : null}
                      </p>
                      <p className="text-[11px]" style={{ color: '#8a7d61' }}>
                        Queued {row.queued_at ? new Date(row.queued_at).toLocaleString('en-IN') : '—'}{row.queued_by ? ` by ${row.queued_by}` : ''}
                      </p>
                    </div>
                    <div className="rounded p-2 text-sm" style={{ background: '#F7F1E3', border: '1px solid #E6DAC0' }}>
                      <p className="font-medium" style={{ color: '#2A241A' }}>Subject: {row.pitch.subject || '—'}</p>
                      <div className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap text-[13px]" style={{ color: '#4a4030' }}>{row.pitch.body || '—'}</div>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold" style={{ color: '#6b5f47' }}>Profiles in this pitch (anonymised)</p>
                      <ul className="mt-1 space-y-1">
                        {(row.pitch.profiles || []).map((p) => <li key={p.ref} className="text-[12px]" style={{ color: '#4a4030' }}>{p.line}</li>)}
                      </ul>
                    </div>
                    <div className="flex justify-end gap-2 pt-1 max-md:[&>button]:flex-1 max-md:[&>button]:min-h-[44px]">
                      <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => discardPitch(row)}>Discard</Button>
                      <Button size="sm" disabled={!!busy} style={{ background: '#AD8A2E', color: '#FFFDF8' }} onClick={() => approvePitch(row)}>Approve and release</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Segment table */}
          <Card className="xl:col-span-2 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-base">Supply map {built && built.built_at ? <span className="text-xs font-normal text-slate-400">— built {new Date(built.built_at).toLocaleString('en-IN')}, {built.candidates_scanned} scanned, {built.candidates_skipped} unclassified</span> : null}</CardTitle>
              <label className="text-xs text-slate-500 flex items-center gap-1"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> show below-minimum</label>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto max-md:hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Segment</th>
                    <th className="text-right px-2 py-2">Ready</th>
                    <th className="text-right px-2 py-2">Total</th>
                    <th className="text-right px-2 py-2">Fresh</th>
                    <th className="text-right px-2 py-2">Median CTC</th>
                    <th className="text-right px-2 py-2">Strength</th>
                    <th className="text-right px-3 py-2">Demand</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSegments.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No segments yet — switch on and rebuild the supply map.</td></tr>
                  )}
                  {visibleSegments.map((s) => (
                    <tr key={s.key} onClick={() => setSelected(selected === s.key ? null : s.key)}
                        className={`cursor-pointer border-t border-slate-100 hover:bg-indigo-50/40 ${selected === s.key ? 'bg-indigo-50' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{s.label}</div>
                        <div className="text-[11px] text-slate-400">{s.key}{s.above_minimum ? '' : ' · below minimum'}</div>
                      </td>
                      <td className="text-right px-2 py-2 font-semibold">{s.ready_count}</td>
                      <td className="text-right px-2 py-2">{s.count}</td>
                      <td className="text-right px-2 py-2">{Math.round((s.freshness || 0) * 100)}%</td>
                      <td className="text-right px-2 py-2">{s.median_ctc_band}</td>
                      <td className="text-right px-2 py-2">{Math.round((s.strength || 0) * 100)}</td>
                      <td className="text-right px-3 py-2 text-xs text-slate-500">{s.last_demand_run ? `${(s.last_demand_run.created || 0) + (s.last_demand_run.updated || 0)} found` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>

            {/* Phones: the same `visibleSegments` rows as the table above,
                stacked as cards. One data path, two presentations. */}
            <CardContent className="hidden max-md:block p-3 space-y-2">
              {visibleSegments.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-400">No segments yet — switch on and rebuild the supply map.</p>
              )}
              {visibleSegments.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSelected(selected === s.key ? null : s.key)}
                  className={`w-full text-left rounded-lg border p-3 ${selected === s.key ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white'}`}
                  data-testid={`segment-card-${s.key}`}
                >
                  <div className="font-medium text-slate-800 break-words">{s.label}</div>
                  <div className="text-[11px] text-slate-400 break-all">{s.key}{s.above_minimum ? '' : ' · below minimum'}</div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Ready</dt><dd className="font-semibold text-slate-800">{s.ready_count}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Total</dt><dd className="text-slate-700">{s.count}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Fresh</dt><dd className="text-slate-700">{Math.round((s.freshness || 0) * 100)}%</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Strength</dt><dd className="text-slate-700">{Math.round((s.strength || 0) * 100)}</dd></div>
                    <div className="col-span-2 flex justify-between gap-2"><dt className="text-slate-400">Median CTC</dt><dd className="text-slate-700">{s.median_ctc_band}</dd></div>
                    <div className="col-span-2 flex justify-between gap-2"><dt className="text-slate-400">Demand</dt><dd className="text-slate-700">{s.last_demand_run ? `${(s.last_demand_run.created || 0) + (s.last_demand_run.updated || 0)} found` : '—'}</dd></div>
                  </dl>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* What we can supply + settings */}
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader className="py-3"><CardTitle className="text-base">{selectedSeg ? 'What we can supply' : 'Pick a segment'}</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-2">
                {selectedSeg ? (
                  <>
                    <p className="font-medium text-slate-800">{selectedSeg.label}</p>
                    <p className="text-slate-600">{selectedSeg.ready_count} ready of {selectedSeg.count} · median {selectedSeg.median_years ?? '—'} yrs · CTC band {selectedSeg.median_ctc_band}</p>
                    <p className="text-[11px] text-slate-400">Notice: {Object.entries(selectedSeg.notice_bands || {}).map(([k, v]) => `${k} ${v}`).join(' · ')}</p>
                    <p className="text-[11px] text-slate-400">Searches: {(selectedSeg.search_titles || []).join(' · ')}</p>
                    <ul className="mt-2 space-y-1">
                      {(selectedSeg.samples || []).map((p) => (
                        <li key={p.ref} className="rounded border border-slate-200 bg-white px-2 py-1 text-[12px] text-slate-700">{p.line}</li>
                      ))}
                    </ul>
                    <p className="text-[11px] text-slate-400">Reference numbers only — no names, employers or contact details leave this screen.</p>
                  </>
                ) : (
                  <p className="text-slate-500">Click a segment row to see its anonymised sample and filter the opportunities.</p>
                )}
              </CardContent>
            </Card>

            {settings && (
              <Card className="shadow-sm">
                <CardHeader className="py-3"><CardTitle className="text-base">Controls</CardTitle></CardHeader>
                <CardContent className="text-sm">
                  <NumberField label="Minimum ready candidates per segment" value={settings.min_segment_size} onChange={(v) => saveSetting({ min_segment_size: v })} />
                  <NumberField label="Ready = notice ≤ days" value={settings.ready_notice_days} onChange={(v) => saveSetting({ ready_notice_days: v })} />
                  <Toggle label="Unknown notice counts as ready" value={settings.unknown_notice_is_ready} onChange={(v) => saveSetting({ unknown_notice_is_ready: v })} />
                  <Toggle label="Job search dry-run (SerpAPI / Perplexity)" hint="On = plan and log the queries, spend nothing" value={settings.job_search_dry_run} onChange={(v) => saveSetting({ job_search_dry_run: v })} />
                  <Toggle label="Apollo dry-run" hint="Company search + job postings; never reveals contacts" value={settings.apollo_dry_run} onChange={(v) => saveSetting({ apollo_dry_run: v })} />
                  <NumberField label="Daily job-search cap" value={settings.daily_job_search_cap} onChange={(v) => saveSetting({ daily_job_search_cap: v })} />
                  <NumberField label="Daily Apollo call cap" value={settings.daily_apollo_call_cap} onChange={(v) => saveSetting({ daily_apollo_call_cap: v })} />
                  <Toggle label="Release queued pitches to the BDE funnel" hint="imported → enriched, capped per day; the BDE still needs approved copy + auto_send" value={settings.release_enabled} onChange={(v) => saveSetting({ release_enabled: v })} />
                  <NumberField label="Release cap per day" value={settings.release_daily_cap} onChange={(v) => saveSetting({ release_daily_cap: v })} />
                  <Toggle label="Nightly job" hint={`Rebuild + demand + replies at ${settings.nightly_hour_ist}:00 IST`} value={settings.nightly_enabled} onChange={(v) => saveSetting({ nightly_enabled: v })} />
                  <div className="flex flex-wrap gap-2 pt-2 max-md:[&>button]:w-full max-md:[&>button]:min-h-[44px]">
                    <Button size="sm" variant="outline" disabled={!!busy || !enabled} onClick={() => release(true)}>Release (dry-run)</Button>
                    <Button size="sm" variant="outline" disabled={!!busy || !enabled} onClick={() => release(false)}>Release now</Button>
                    <Button size="sm" variant="outline" disabled={!!busy} onClick={syncReplies}>Sync replies</Button>
                  </div>
                  {usage && (
                    <p className="text-[11px] text-slate-400 pt-2">
                      Last {usage.days} days: {usage.real_units} real calls (~${usage.cost_estimate_usd}), {usage.dry_run_units} dry-run. Today: job search {usage.today.job_search}/{usage.caps.daily_job_search_cap}, Apollo {usage.today.apollo}/{usage.caps.daily_apollo_call_cap}.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Opportunities */}
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 py-3">
            <CardTitle className="text-base">Opportunities {selectedSeg ? <span className="text-xs font-normal text-slate-400">— {selectedSeg.label}</span> : <span className="text-xs font-normal text-slate-400">— all segments</span>}</CardTitle>
            <div className="flex items-center gap-2 text-xs max-md:w-full max-md:flex-wrap max-md:[&>select]:w-full max-md:[&>select]:min-h-[44px]">
              <select className="border rounded px-2 py-1" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">all statuses</option>
                {['new', 'existing_client', 'queued', 'contacted_pending', 'contacted', 'replied', 'mandate', 'placed', 'dismissed'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="border rounded px-2 py-1" value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                {templates.map((t) => <option key={t.key} value={t.key}>{t.label}{approvedKeys.has(t.key) ? ' ✓' : ' (not approved)'}</option>)}
              </select>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto max-md:hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Company · role</th>
                  <th className="text-left px-2 py-2">Source</th>
                  <th className="text-right px-2 py-2">Posted</th>
                  <th className="text-right px-2 py-2">Est. fee</th>
                  <th className="text-right px-2 py-2">Score</th>
                  <th className="text-left px-2 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {opps.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No opportunities. Run "Find demand" — local sources (ATS jobs, AI Job Search history) are free; external searches are dry-run until you switch them on.</td></tr>
                )}
                {opps.map((o) => {
                  const blocked = o.status === 'existing_client' || (o.dedupe && o.dedupe.status !== 'new');
                  return (
                    <tr key={o.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{o.company} <span className="text-slate-400">·</span> {o.title}</div>
                        <div className="text-[11px] text-slate-400">
                          {o.location || '—'}{o.url ? <> · <a href={o.url} target="_blank" rel="noreferrer" className="text-indigo-600 underline">posting</a></> : null}
                          {o.contact && o.contact.email ? ' · contact on file' : ' · no contact yet'}
                          {o.dedupe && o.dedupe.status === 'existing_client' ? ` · EXISTING CLIENT ${o.dedupe.client_name || ''} → account owner` : ''}
                          {o.dedupe && o.dedupe.status === 'bde_lead' ? ` · already a BDE lead (${o.dedupe.stage})` : ''}
                          {o.dedupe && o.dedupe.status === 'suppressed' ? ' · contact opted out' : ''}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-500">{o.source}</td>
                      <td className="px-2 py-2 text-right text-xs">{o.posted_days === null || o.posted_days === undefined ? '—' : `${o.posted_days}d`}</td>
                      <td className="px-2 py-2 text-right text-xs" title={o.est_fee ? o.est_fee.basis : ''}>{o.est_fee ? fmtInr(o.est_fee.low) : '—'}</td>
                      <td className="px-2 py-2 text-right font-semibold">{o.score}</td>
                      <td className="px-2 py-2"><span className={`inline-block rounded border px-2 py-0.5 text-[11px] ${STATUS_TONE[o.status] || 'bg-slate-50 text-slate-600'}`}>{o.status}</span></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => preview(o)}>Pitch</Button>
                        <Button size="sm" variant="outline" disabled={!!busy || !enabled || blocked || !!o.lead_id} onClick={() => queue(o)}>Queue</Button>
                        {o.lead_id && o.status !== 'mandate' && o.status !== 'placed' ? <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => mandate(o)}>Mandate</Button> : null}
                        {o.status !== 'dismissed' ? <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => dismiss(o)}>×</Button> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>

          {/* Phones: the same `opps` rows as the table above, stacked as cards
              with the four actions as real tap targets. */}
          <CardContent className="hidden max-md:block p-3 space-y-2">
            {opps.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-400">No opportunities. Run "Find demand" — local sources (ATS jobs, AI Job Search history) are free; external searches are dry-run until you switch them on.</p>
            )}
            {opps.map((o) => {
              const blocked = o.status === 'existing_client' || (o.dedupe && o.dedupe.status !== 'new');
              return (
                <div key={`m-${o.id}`} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`opportunity-card-${o.id}`}>
                  <div className="font-medium text-slate-800 break-words">{o.company}</div>
                  <div className="text-sm text-slate-600 break-words">{o.title}</div>
                  <div className="mt-1 text-[11px] text-slate-400 break-words">
                    {o.location || '—'}{o.url ? <> · <a href={o.url} target="_blank" rel="noreferrer" className="text-indigo-600 underline">posting</a></> : null}
                    {o.contact && o.contact.email ? ' · contact on file' : ' · no contact yet'}
                    {o.dedupe && o.dedupe.status === 'existing_client' ? ` · EXISTING CLIENT ${o.dedupe.client_name || ''} → account owner` : ''}
                    {o.dedupe && o.dedupe.status === 'bde_lead' ? ` · already a BDE lead (${o.dedupe.stage})` : ''}
                    {o.dedupe && o.dedupe.status === 'suppressed' ? ' · contact opted out' : ''}
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Source</dt><dd className="text-slate-700 truncate">{o.source}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Posted</dt><dd className="text-slate-700">{o.posted_days === null || o.posted_days === undefined ? '—' : `${o.posted_days}d`}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Est. fee</dt><dd className="text-slate-700">{o.est_fee ? fmtInr(o.est_fee.low) : '—'}</dd></div>
                    <div className="flex justify-between gap-2"><dt className="text-slate-400">Score</dt><dd className="font-semibold text-slate-800">{o.score}</dd></div>
                  </dl>
                  <div className="mt-2">
                    <span className={`inline-block rounded border px-2 py-0.5 text-[11px] ${STATUS_TONE[o.status] || 'bg-slate-50 text-slate-600'}`}>{o.status}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 [&>button]:w-full [&>button]:min-h-[44px]">
                    <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => preview(o)}>Pitch</Button>
                    <Button size="sm" variant="outline" disabled={!!busy || !enabled || blocked || !!o.lead_id} onClick={() => queue(o)}>Queue</Button>
                    {o.lead_id && o.status !== 'mandate' && o.status !== 'placed' ? <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => mandate(o)}>Mandate</Button> : null}
                    {o.status !== 'dismissed' ? <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => dismiss(o)}>Dismiss</Button> : null}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Pitch preview */}
        {pitch && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6 overflow-y-auto max-md:p-3 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]" onClick={() => setPitch(null)}>
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Pitch preview — {pitch.opportunity.company}</h2>
                  <p className="text-xs text-slate-500">Template {pitch.template_key} · {pitch.approval ? (pitch.approval.owner_approved ? 'owner-approved' : `NOT approved (${pitch.approval.reason}) — the BDE will refuse to send until approved via /api/bde/copy/approve`) : 'approval state unavailable (Track C copy module not installed)'}</p>
                  {pitch.blocked ? <p className="text-xs text-amber-700 mt-1">Blocked: {JSON.stringify(pitch.blocked_reason)}</p> : null}
                </div>
                <Button variant="ghost" onClick={() => setPitch(null)}>Close</Button>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
                <p className="font-medium text-slate-800">Subject: {pitch.pitch.subject}</p>
                <pre className="whitespace-pre-wrap font-sans text-slate-700 mt-2">{pitch.pitch.body}</pre>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-600">Profiles in this pitch (anonymised)</p>
                <ul className="mt-1 space-y-1">{pitch.profiles.map((p) => <li key={p.ref} className="text-[12px] text-slate-700">{p.line}</li>)}</ul>
                <p className="text-[11px] text-slate-400 mt-1">The review link is created only when you press Queue (72-hour, name and employer hidden, CTC withheld, OTP wall). This preview stored nothing.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPitch(null)}>Cancel</Button>
                <Button disabled={!!busy || !enabled || pitch.blocked} onClick={() => queue(pitch.opportunity)}>Queue for the BDE</Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
